import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import MaHeaderUserMenu from '@/components/MaHeaderUserMenu/MaHeaderUserMenu';
import { useProfile } from '../ProfileContext';
import { performHardLogout } from '@/lib/authLogout';
import { activityTracker } from '../services/activityTracker';
import { getAvatarUrl, getDisplayName, getEmail, getFullName } from '../utils/profileUtils';
import { getMaHeaderInitials } from '@/utils/maHeaderUser';
import { getAppStage } from '@/lib/appStage';

/**
 * Super Admin navbar menu — delegates to Master Account `MaHeaderUserMenu` (+ SA wiring).
 */
const ProfileDropdown: React.FC = () => {
  const { profile } = useProfile();
  const navigate = useNavigate();
  const { logout, user } = useAuth0();

  const stage = getAppStage();

  const userInfo = useMemo(
    () => ({
      firstName: profile?.firstName,
      lastName: profile?.lastName,
      email: profile?.email || user?.email,
      avatar: getAvatarUrl(profile, stage),
    }),
    [profile, stage, user?.email]
  );

  const userDisplayName = useMemo(() => getDisplayName(profile), [profile]);
  const userInitials = useMemo(() => getMaHeaderInitials(userInfo, user), [userInfo, user]);

  const handleSignOut = async () => {
    if (user && profile) {
      activityTracker
        .logLogout(user.sub || '', getFullName(profile) || user.name || '', 'employee', getEmail(profile))
        .catch((error: unknown) => {
          console.error('Failed to log logout activity:', error);
        });
    }
    await performHardLogout(logout);
  };

  return (
    <MaHeaderUserMenu
      userInfo={userInfo}
      userInitials={userInitials}
      userDisplayName={userDisplayName}
      onMyProfile={() => navigate('/account/profile')}
      onSignOut={handleSignOut}
    />
  );
};

export default ProfileDropdown;
